import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Reusable DashboardCard component used across role dashboards (Employee, Manager, HR, Team Lead).
 */
export const DashboardCard = ({
    card,
    id,
    title,
    count,
    icon,
    p,
    s,
    primaryColor,
    secondaryColor,
    date,
    path,
    dialog,
    onClick,
    sx = {}
}) => {
    const primary = p || primaryColor || card?.p || card?.primaryColor || '#3b82f6';
    const secondary = s || secondaryColor || card?.s || card?.secondaryColor || primary;
    const cardTitle = title || card?.title;
    const cardCount = count ?? card?.count ?? 0;
    const cardIcon = icon || card?.icon;
    const cardDate = date || card?.date;
    const cardId = id ?? card?.id;

    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <Card
            onClick={handleClick}
            sx={{
                borderRadius: '24px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(255,255,255,0.3)',
                px: 3,
                py: 3,
                background: '#fff',
                containerType: 'inline-size',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(135deg, ${alpha(primary, 0.08)}, ${alpha(secondary, 0.08)})`,
                    clipPath: 'circle(70% at 100% 0%)',
                },
                '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: `0 20px 40px ${alpha(primary, 0.15)}`,
                },
                ...sx
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1.5,
                    '@container (max-width: 200px)': {
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                    }
                }}
            >
                <Box>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: `linear-gradient(135deg, ${primary}, ${secondary})`, mb: 1.5
                    }}>
                        {cardIcon && React.isValidElement(cardIcon)
                            ? React.cloneElement(cardIcon, { sx: { fontSize: 24, color: '#fff', ...(cardIcon.props?.sx || {}) } })
                            : cardIcon}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {cardTitle}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: primary,
                            fontSize: (cardId === 5) && (cardTitle === 'Upcoming Holiday')? '1.2rem' : '2rem',
                            lineHeight: 1.2
                        }}
                    >
                        {cardCount}
                    </Typography>

                    {cardDate && (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                color: '#64748b',
                                fontWeight: 700,
                                mt: 0.5
                            }}
                        >
                            {cardDate}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

export default DashboardCard;
